import * as Sentry from '@sentry/react';

const API_BASE_URL = 'https://api.bookingkit.de/v3';
const LOCAL_STORAGE_TOKEN_KEY = 'bookingKitToken';
const MAX_SECONDS_BEFORE_EXPIRY_FOR_TOKEN_RENEWAL = 300;

export interface BookingKitPrice {
  title: string;
  currency: string;
  value: string;
}

export interface BookingKitDate {
  id: string;
  date: string;
  available_slots: number;
}

export interface EventDetails {
  id: string;
  name: string;
  prices: BookingKitPrice[];
}

export type EventDetailsWithDates = EventDetails & {
  dates: Array<BookingKitDate>;
};

interface TokenData {
  token: string;
  expiryTime: number;
}

interface ApiToken {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

function getTokenDataFromLocalStorage(): TokenData | null {
  try {
    const tokenDataStr = window.localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
    if (tokenDataStr) {
      const tokenData = JSON.parse(tokenDataStr);
      if (
        tokenData === null ||
        typeof tokenData !== 'object' ||
        typeof tokenData.token !== 'string' ||
        typeof tokenData.expiryTime !== 'number'
      ) {
        throw new Error('Found tokenData with bad structure in localStorage: ' + tokenDataStr);
      }
      return tokenData as TokenData;
    }
    return null;
  } catch (error) {
    window.localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}

export class BookingKitClient {
  private tokenData: TokenData | null = null;
  private tokenPromise: Promise<ApiToken> | null = null;

  private async getApiToken(): Promise<ApiToken> {
    if (!this.tokenPromise) {
      this.tokenPromise = this.fetchApiToken();
    }
    return this.tokenPromise;
  }

  private async fetchApiToken(): Promise<ApiToken> {
    const resp = await fetch('/.netlify/functions/get-bookingkit-token', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (resp.status !== 200) {
      throw new Error(`Got response with status code ${resp.status} for get-bookingkit-token`);
    }
    const apiToken: ApiToken = await resp.json();
    this.tokenPromise = null;
    return apiToken;
  }

  private async makeRequest(endpoint: string, params: Record<string, string> = {}) {
    // If tokenData not initialized, try to get it from localStorage
    if (!this.tokenData) {
      this.tokenData = getTokenDataFromLocalStorage();
    }
    // If tokenData expiryTime is close, clear tokenData including localStorage
    if (
      this.tokenData &&
      Date.now() + MAX_SECONDS_BEFORE_EXPIRY_FOR_TOKEN_RENEWAL * 1000 > this.tokenData.expiryTime
    ) {
      this.tokenData = null;
      window.localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    }
    // If we don't have a valid tokenData, get one from the api
    if (!this.tokenData) {
      const apiToken = await this.getApiToken();
      this.tokenData = {
        token: apiToken.access_token,
        expiryTime: Date.now() + apiToken.expires_in * 1000,
      };
      window.localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, JSON.stringify(this.tokenData));
    }

    const url = new URL(`${API_BASE_URL}${endpoint}`);
    for (const key of Object.keys(params)) {
      url.searchParams.append(key, params[key]);
    }
    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${this.tokenData.token}`,
      },
    });

    if (!response.ok) {
      console.error({ endpoint, params });
      throw new Error(`Error making request: ${response.statusText}`);
    }

    return response.json();
  }

  async getEventDetails(eventId: string, options: { withDates?: false }): Promise<EventDetails>;

  async getEventDetails(
    eventId: string,
    options: { withDates: true },
  ): Promise<EventDetailsWithDates>;

  async getEventDetails(
    eventId: string,
    options: { withDates?: boolean } = {},
  ): Promise<EventDetails | EventDetailsWithDates> {
    const { withDates = false } = options;
    if (withDates) {
      // TODO Fetch only needed params
      const [event, eventDates] = await Promise.all([
        this.makeRequest(`/events/${eventId}`) as Promise<EventDetails>,
        this.makeRequest(`/events/${eventId}/dates`) as Promise<Array<BookingKitDate>>,
      ]);
      return {
        ...event,
        dates: eventDates,
      };
    } else {
      // TODO Fetch only needed params
      return this.makeRequest(`/events/${eventId}`) as Promise<EventDetails>;
    }
  }
}

export default new BookingKitClient();
